import {SubAPI} from "./SubAPI";
import {Product} from "./Products";
import {Paginated} from "./Users";
import {BuildSolution} from "./Manufacturing";
import {InventoryKind} from "./Logistics";

export class ConfigureSourcing extends SubAPI {
    upsert(input: Partial<ConfigureSourcingGroup>) {
        return this.fetcher.post<ConfigureSourcingGroup>("/api/logistics/configure-sourcing", input)
    }

    get(input: {id: number}) {
        return this.fetcher.get<Config>("/api/logistics/configure-sourcing", input)
    }

    list(input: {
        offset?: number
        search?: string
        project?: number;
    }) {
        return this.fetcher.get<Paginated<Config>>("/api/logistics/configure-sourcing/list", input)
    }

    addItems(input: {
        group: number;
        inventoryRequests: number[]
    }) {
        return this.fetcher.post("/api/logistics/configure-sourcing/group/add-items", input)
    }

    removeItems(input: {
        group: number;
        inventoryRequests: number[]
    }) {
        return this.fetcher.post("/api/logistics/configure-sourcing/group/remove-items", input)
    }

    listGroups(input: {
        config: number;
    }) {
        return this.fetcher.get<ConfigureSourcingGroup[]>("/api/logistics/configure-sourcing/groups", input)
    }

    upsertGroups(input: ConfigureSourcingGroup) {
        return this.fetcher.post<ConfigureSourcingGroup>("/api/logistics/configure-sourcing/group", input)
    }

    listSourceItems(input: {
        config: number
        offset?: number
        search?: string;
    }) {
        return this.fetcher.get<Paginated<ConfigureSrcItem>>("/api/logistics/configure-sourcing/src-items", input)
    }

    upsertSourceItem(input: Partial<ConfigureSrcItem>) {
        return this.fetcher.post<ConfigureSrcItem>("/api/logistics/configure-sourcing/src-item", input)
    }

    listChildren(input: {
        offset?: number
        config?: number;
    }) {
        return this.fetcher.get<Paginated<ConfigureChildItem>>("/api/logistics/configure-sourcing/children", input)
    }

    previewItem(input: {
        srcItem: number;
    }) {
        return this.fetcher.get<CalcItem>("/api/logistics/configure-sourcing/preview-item", input)
    }

    build(input: {
        config: number;
        preview?: boolean;
        complete?: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/logistics/configure-sourcing/build", input);
    }

    listOutput(input: {
        config: number;
    }) {
        return this.fetcher.get<OutputItem[]>("/api/logistics/configure-sourcing/previews", input)
    }

}

export type OutputItem = CalcItem & {
    id: number;
    config: number;
    srcItem: number;
    srcItemObj: ConfigureSrcItem;

    updatedAt: string;
    createdAt: string;
}

export type CalcItem = {
    build: BuildSolution | null;
    outsource: OutsourceItem[] | null;
}

export type OutsourceItem = {
    product: Product;
    name: string;
    qty: number;
}

export type Config = {
    id: number;
    project: number;
    projectName: string;
    projectNumber: number;

    poTykel: number | null;
    tykelPoNumber: string | null;

    poSupplier: number | null;
    supplierPoNumber: string | null;
}

export type ConfigureSourcingGroup = {
    id: number;
    config: number;
    name: string;
    productKind: InventoryKind

    archived: boolean;
    updatedAt: string;
    updatedBy: number;

    outsourceAll: boolean | null;

    outsourceAnchors: boolean | null;
    outsourceAnchorsDetails: ChildSwitchDetails;

    outsourceFrameConstruction: boolean | null;
    outsourceFrameConstructionDetails: ChildSwitchDetails;

    outsourceHwPreps: boolean | null;
    outsourceHwPrepsDetails: ChildSwitchDetails;

    outsourceDoorSeam: boolean | null;
    outsourceDoorSeamDetails: ChildSwitchDetails;

    outsourceDoorElevation: boolean | null;
    outsourceDoorElevationDetails: ChildSwitchDetails;

    outsourceDoorLabel: boolean | null;
    outsourceDoorLabelDetails: ChildSwitchDetails;

    items?: ConfigureSrcItem[];
    addItems?: number[];
    removeItems?: number[];
}

export type ChildSwitchDetails = {
    default: boolean;
    hasOverride: boolean;
    items?: ChildSwitchItem[];
}

export type ChildSwitchItem = {
    id: number;
    config: number;
    sourceGroup: number;

    name: string;
    key: string;
    category: "anchors" | "frame-construction" | "hw-preps" | "screens";
    outsource: boolean | null;

    archived: boolean;
    createdAt: string;
    updatedAt: string;
}

export type ConfigureSrcItem = {
    id: number;
    openingPhase?: string | null;

    config: number;
    sourceGroup: number;
    sourceGroupName: string;

    openingName: string;

    srcRequest: number;
    entireProductOutsourced: boolean;

    manufacturer: number;
    product: number;
    purchaseOrder: number | null;
    poNumber: string | null;

    frame: Product["frame"] | null;
    door: Product["door"] | null;

    qty: number;

    archived: boolean;
    updatedAt: string;
    updatedBy: number;
}

export type ConfigureChildItem = {
    id: number;
    openingName: string | null;
    name: string;
    status: string;
    purchaseOrder: number | null;
    poNumber: string | null;

    subComponent: number | null;
    entireProductOutsourced: boolean | null;
}